import * as Cesium from "cesium";
import { cloneEntity, getUsernameAndUserIdFormat, showToast } from "../Utility";
import Type from "../Types";

export function joinTwoSelectedPolylinesOnce({ selectedEntitiesRef, thresholdMeters = 0.001, pushUndo, userData }) {
  try {
    const sel = selectedEntitiesRef?.current || [];
    if (!Array.isArray(sel) || sel.length < 2) {
      showToast("Select at least two line features", Type.NOTIFICATION.WARNING);
      return false;
    }

    // all in same collection
    const firstCol = sel[0]?.entityCollection;
    if (!firstCol) {
      showToast("Invalid data source", Type.NOTIFICATION.ERROR);
      return false;
    }
    for (let i = 1; i < sel.length; i++) {
      if (!sel[i]?.entityCollection || sel[i].entityCollection !== firstCol) {
        showToast("All features must be in the same data source", Type.NOTIFICATION.WARNING);
        return false;
      }
    }

    // all must be polylines
    const entities = sel.filter((e) => e?.polyline);
    if (entities.length < 2) {
      showToast("All selections must be polylines", Type.NOTIFICATION.WARNING);
      return false;
    }

    const Ces = typeof Cesium !== "undefined" ? Cesium : null;
    if (!Ces) {
      showToast("Cesium is not available", Type.NOTIFICATION.ERROR);
      return false;
    }

    const col = firstCol;
    const dsRef = col?.owner || col?._owner;
    const modifier = getUsernameAndUserIdFormat(userData);

    // main loop
    let changed = true;
    const undoPackets = [];
    let working = entities.slice(); // array of live entities

    while (changed && working.length > 1) {
      changed = false;

      // build position cache
      const posCache = new Map();
      for (const ent of working) {
        const arr = _getPositionsArray(ent);
        if (!Array.isArray(arr) || arr.length < 2) {
          showToast("Polyline with <2 points skipped", Type.NOTIFICATION.WARNING);
          return false;
        }
        posCache.set(ent.id, arr);
      }

      // find best pair to join
      let bestPair = null;

      for (let i = 0; i < working.length; i++) {
        for (let j = i + 1; j < working.length; j++) {
          const A = working[i];
          const B = working[j];
          const positionsA = posCache.get(A.id);
          const positionsB = posCache.get(B.id);

          const Astart = positionsA[0];
          const Aend = positionsA[positionsA.length - 1];
          const Bstart = positionsB[0];
          const Bend = positionsB[positionsB.length - 1];

          const d_Ae_Bs = Ces.Cartesian3.distance(Aend, Bstart);
          const d_As_Be = Ces.Cartesian3.distance(Astart, Bend);
          const d_As_Bs = Ces.Cartesian3.distance(Astart, Bstart);
          const d_Ae_Be = Ces.Cartesian3.distance(Aend, Bend);

          const candidates = [
            { type: "Ae-Bs", d: d_Ae_Bs, A, B, positionsA, positionsB },
            { type: "As-Be", d: d_As_Be, A, B, positionsA, positionsB },
            { type: "As-Bs", d: d_As_Bs, A, B, positionsA, positionsB },
            { type: "Ae-Be", d: d_Ae_Be, A, B, positionsA, positionsB },
          ];

          for (const c of candidates) {
            if (c.d < thresholdMeters) {
              if (!bestPair || c.d < bestPair.d) {
                bestPair = c;
              }
            }
          }
        }
      }

      // no more joinable pairs
      if (!bestPair) break;

      // join this best pair
      const { A, B, positionsA, positionsB, type } = bestPair;

      let joined;
      switch (type) {
        case "Ae-Bs":
          joined = _concatNoDup(positionsA, positionsB, thresholdMeters);
          break;
        case "As-Be":
          joined = _concatNoDup(positionsB, positionsA, thresholdMeters);
          break;
        case "As-Bs":
          joined = _concatNoDup(positionsA.slice().reverse(), positionsB, thresholdMeters);
          break;
        case "Ae-Be":
          joined = _concatNoDup(positionsA, positionsB.slice().reverse(), thresholdMeters);
          break;
        default:
          showToast("Unexpected join case", Type.NOTIFICATION.ERROR);
          return false;
      }
      if (!joined || joined.length < 2) {
        showToast("Failed to build joined path", Type.NOTIFICATION.ERROR);
        return false;
      }

      const lenA = _polylineLengthMeters(positionsA);
      const lenB = _polylineLengthMeters(positionsB);
      const keeper = lenA >= lenB ? A : B;
      const removee = lenA >= lenB ? B : A;

      const keeperSnapshotBefore = cloneEntity(keeper);
      const removeeSnapshot = cloneEntity(removee);

      keeper.polyline.positions = new Ces.ConstantProperty(joined);

      if (modifier) {
        const currentProps = keeper.properties?.getValue ? keeper.properties.getValue(Ces.JulianDate.now()) || {} : keeper.properties || {};
        keeper.properties = new Ces.PropertyBag({
          ...currentProps,
          modifier,
        });
      }

      col.remove(removee);

      // undo stack
      if (typeof pushUndo === "function") {
        undoPackets.push({
          type: "modify",
          entitySnapshot: keeperSnapshotBefore,
          entityId: keeper.id,
          dsRef,
        });
        undoPackets.push({
          type: "delete",
          entitySnapshot: removeeSnapshot,
          entityId: removee.id,
          dsRef,
        });
      }

      // update working list
      working = working.filter((e) => e.id !== removee.id);
      changed = true;
    }

    // push all undo ops in order
    if (typeof pushUndo === "function") {
      for (const pkt of undoPackets) {
        pushUndo(pkt);
      }
    }

    selectedEntitiesRef.current = working;
    if (working.length === 1) {
      showToast("Lines joined into 1 feature", Type.NOTIFICATION.SUCCESS);
    } else {
      showToast(`Lines joined. Result count: ${working.length}`, Type.NOTIFICATION.SUCCESS);
    }
    return true;
  } catch (err) {
    console.error(err);
    showToast("Join failed due to an unexpected error", Type.NOTIFICATION.ERROR);
    return false;
  }
}

// helpers
function _getPositionsArray(entity) {
  const prop = entity?.polyline?.positions;
  if (!prop) return null;
  if (prop && typeof prop.getValue === "function") {
    const time = typeof Cesium !== "undefined" ? Cesium.JulianDate.now() : undefined;
    const v = prop.getValue(time);
    return Array.isArray(v) ? v.slice() : null;
  }
  if (Array.isArray(prop)) return prop.slice();
  return null;
}

function _concatNoDup(a, b, thresholdMeters) {
  if (!a?.length) return b?.slice() || [];
  if (!b?.length) return a?.slice() || [];
  const Ces = typeof Cesium !== "undefined" ? Cesium : null;
  if (!Ces) return a.concat(b);
  const dist = Ces.Cartesian3.distance(a[a.length - 1], b[0]);
  if (dist < thresholdMeters) return a.concat(b.slice(1));
  return a.concat(b);
}

function _polylineLengthMeters(positions) {
  const Ces = typeof Cesium !== "undefined" ? Cesium : null;
  if (!Ces || !Array.isArray(positions) || positions.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < positions.length; i++) {
    sum += Ces.Cartesian3.distance(positions[i - 1], positions[i]);
  }
  return sum;
}
